import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Pagination, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../components/BackendErrorMessage';
import CandidatoSolicitacaoSensivelViewModel, {
  SolicitacaoAlteracaoSensivelCandidato
} from '../../viewmodel/CandidatoSolicitacaoSensivelViewModel';

const SolicitacoesAlteracaoSensivelPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [items, setItems] = useState<SolicitacaoAlteracaoSensivelCandidato[]>([]);
  const [selected, setSelected] = useState<SolicitacaoAlteracaoSensivelCandidato | null>(null);
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);

  const carregar = async () => {
    try {
      setLoading(true);
      const resp = await CandidatoSolicitacaoSensivelViewModel.listarPendentes(page, size);
      setItems(resp.content || []);
      setTotalPages(resp.totalPages || 0);
      setTotalElements(resp.totalElements || 0);
    } catch (error) {
      toast.error(BackendErrorMessage(error) || 'Erro ao carregar solicitações pendentes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, [page]);

  const aprovar = async (id: string) => {
    try {
      setSaving(true);
      await CandidatoSolicitacaoSensivelViewModel.aprovar(id);
      toast.success('Solicitação aprovada.');
      await carregar();
    } catch (error) {
      toast.error(BackendErrorMessage(error) || 'Erro ao aprovar solicitação.');
    } finally {
      setSaving(false);
    }
  };

  const rejeitar = async () => {
    if (!selected) return;
    if (!motivo.trim()) {
      toast.error('Informe o motivo da rejeição.');
      return;
    }

    try {
      setSaving(true);
      await CandidatoSolicitacaoSensivelViewModel.rejeitar(selected.id, motivo.trim());
      toast.success('Solicitação rejeitada.');
      setSelected(null);
      setMotivo('');
      await carregar();
    } catch (error) {
      toast.error(BackendErrorMessage(error) || 'Erro ao rejeitar solicitação.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mt-4">
      <h2>Solicitações Sensíveis de Candidato</h2>
      <p className="text-muted">Avaliação de requests pendentes para alteração de status e deleção do Candidato.</p>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Data</th>
                <th>Processo</th>
                <th>Candidato</th>
                <th>Inscrição / Edital</th>
                <th>Solicitante</th>
                <th>Tipo</th>
                <th>Status Atual</th>
                <th>Status Solicitado</th>
                <th>Jira</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.length > 0 ? (
                items.map((item) => (
                  <tr key={item.id}>
                    <td>{new Date(item.dataCriacao).toLocaleString('pt-BR')}</td>
                    <td>{item.processoIdentificador || '-'}</td>
                    <td>{item.candidatoNome || '-'}</td>
                    <td>
                      {item.inscricoesEditais && item.inscricoesEditais.length > 0
                        ? item.inscricoesEditais.map((v, idx) => (
                            <div key={`${item.id}-insc-${idx}`}>{v}</div>
                          ))
                        : '-'}
                    </td>
                    <td>{item.solicitanteNome || '-'}</td>
                    <td>{item.tipo === 'DELETAR' ? 'Deleção do Candidato' : 'Alteração de status'}</td>
                    <td>{item.statusAtual}</td>
                    <td>{item.statusSolicitado || '-'}</td>
                    <td>{item.jiraTicket}</td>
                    <td className="d-flex gap-2">
                      <Button size="sm" variant="success" disabled={saving} onClick={() => aprovar(item.id)}>
                        Aprovar
                      </Button>
                      <Button size="sm" variant="danger" disabled={saving} onClick={() => setSelected(item)}>
                        Rejeitar
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="text-center">
                    Nenhuma solicitação pendente.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <Pagination>
              <Pagination.First onClick={() => setPage(0)} disabled={page === 0} />
              <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page === 0} />
              {[...Array(totalPages)].map((_, i) => (
                <Pagination.Item key={i} active={i === page} onClick={() => setPage(i)}>
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1} />
              <Pagination.Last onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} />
            </Pagination>
          )}

          <small className="text-muted">Total pendente: {totalElements}</small>
        </>
      )}

      <Modal show={!!selected} onHide={() => { setSelected(null); setMotivo(''); }}>
        <Modal.Header closeButton>
          <Modal.Title>Rejeitar Solicitação</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Motivo</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Informe o motivo da rejeição"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setSelected(null); setMotivo(''); }} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={rejeitar} disabled={saving}>
            Rejeitar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SolicitacoesAlteracaoSensivelPage;
